import React, { useState, useRef } from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { X, Upload, AlertCircle, Download, FileText, Database, RefreshCw } from 'lucide-react';
import { Button } from '../ui/Button';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';

interface ImportRiskProspectsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ImportRiskProspectsModal: React.FC<ImportRiskProspectsModalProps> = ({ isOpen, onClose }) => {
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const downloadTemplate = () => {
    // Create CSV content
    const headers = [
      'First Name',
      'Last Name',
      'Email',
      'Phone',
      'Portfolio Value',
      'Risk Score',
      'Stocks Percentage',
      'Bonds Percentage',
      'Cash Percentage',
      'Other Percentage',
      'Lead Source',
      'Notes'
    ].join(',');

    // Sample data row
    const sampleData = [
      'John',
      'Smith',
      'john.smith@example.com',
      '(555) 123-4567',
      '250000',
      '65',
      '60',
      '25',
      '10',
      '5',
      'whatismyrisknumber.com',
      'Interested in retirement planning'
    ].join(',');

    const blob = new Blob([headers + '\n' + sampleData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'risk_number_prospects_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const validateCSV = async (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const rows = text.split('\n');
          const headers = rows[0].split(',').map(h => h.trim());
          
          // Validate required headers
          const requiredHeaders = ['First Name', 'Last Name', 'Email', 'Risk Score'];
          const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
          
          if (missingHeaders.length > 0) {
            reject(`Missing required columns: ${missingHeaders.join(', ')}`);
            return;
          }

          // Parse and validate data rows
          const data = rows.slice(1)
            .filter(row => row.trim())
            .map((row, index) => {
              const values = row.split(',').map(v => v.trim());
              const record: any = {};
              
              headers.forEach((header, i) => {
                record[header] = values[i] || '';
              });

              // Validate required fields
              if (!record['First Name']) throw new Error(`Missing First Name in row ${index + 2}`);
              if (!record['Last Name']) throw new Error(`Missing Last Name in row ${index + 2}`);
              if (!record['Email']) throw new Error(`Missing Email in row ${index + 2}`);
              if (!record['Risk Score']) throw new Error(`Missing Risk Score in row ${index + 2}`);
              
              // Validate email format
              const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
              if (record['Email'] && !emailRegex.test(record['Email'])) {
                throw new Error(`Invalid email format in row ${index + 2}`);
              }

              // Validate numeric fields
              const numericFields = ['Portfolio Value', 'Risk Score', 'Stocks Percentage', 'Bonds Percentage', 'Cash Percentage', 'Other Percentage'];
              numericFields.forEach(field => {
                if (record[field] && isNaN(Number(record[field]))) {
                  throw new Error(`Invalid numeric value for ${field} in row ${index + 2}`);
                }
              });

              return record;
            });

          resolve(data);
        } catch (error) {
          reject(error.message);
        }
      };

      reader.onerror = () => reject('Error reading file');
      reader.readAsText(file);
    });
  };

  const importMutation = useMutation({
    mutationFn: async (data: any[]) => {
      // In a real implementation, this would insert data into your database
      // For this demo, we'll simulate a successful import with a delay
      
      setImportProgress(0);
      setIsImporting(true);
      
      // Process records in batches to show progress
      const batchSize = 5;
      const totalBatches = Math.ceil(data.length / batchSize);
      
      for (let i = 0; i < data.length; i += batchSize) {
        const batch = data.slice(i, i + batchSize);
        
        // Simulate API call to insert batch
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Update progress
        setImportProgress(Math.min(100, Math.round(((i + batch.length) / data.length) * 100)));
      }
      
      // In a real implementation, you would insert the data into your database
      // For example:
      /*
      const { data: result, error } = await supabase
        .from('risk_prospects')
        .insert(
          data.map(record => ({
            first_name: record['First Name'],
            last_name: record['Last Name'],
            email: record['Email'],
            phone: record['Phone'] || null,
            portfolio_value: record['Portfolio Value'] ? Number(record['Portfolio Value']) : null,
            risk_score: Number(record['Risk Score']),
            asset_allocation: {
              stocks: record['Stocks Percentage'] ? Number(record['Stocks Percentage']) : 0,
              bonds: record['Bonds Percentage'] ? Number(record['Bonds Percentage']) : 0,
              cash: record['Cash Percentage'] ? Number(record['Cash Percentage']) : 0,
              other: record['Other Percentage'] ? Number(record['Other Percentage']) : 0
            },
            lead_source: record['Lead Source'] || 'whatismyrisknumber.com',
            lead_status: 'new',
            submission_date: new Date().toISOString(),
            notes: record['Notes'] ? [record['Notes']] : []
          }))
        );
      
      if (error) throw error;
      */
      
      return data.length;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: ['riskProspects'] });
      toast.success(`Successfully imported ${count} prospects`);
      setFile(null);
      setImportProgress(0);
      setIsImporting(false);
      onClose();
    },
    onError: (error) => {
      toast.error(`Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setImportProgress(0);
      setIsImporting(false);
    }
  });

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      setError('Please upload a CSV file');
      return;
    }

    // Validate file size (50MB)
    if (file.size > 50 * 1024 * 1024) {
      setError('File size must be less than 50MB');
      return;
    }

    setFile(file);
    setError(null);
    setIsValidating(true);

    try {
      const data = await validateCSV(file);
      setIsValidating(false);
      
      // Show confirmation with data summary
      const confirmImport = window.confirm(
        `Ready to import ${data.length} prospects. Continue?`
      );
      
      if (confirmImport) {
        importMutation.mutate(data);
      }
    } catch (err) {
      setError(err.toString());
      setIsValidating(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (fileInputRef.current) {
        fileInputRef.current.files = e.dataTransfer.files;
        handleFileChange({ target: { files: e.dataTransfer.files } } as any);
      }
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />
      
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="relative w-full max-w-2xl bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-xl font-semibold text-gray-900">Import Risk Number Prospects</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-6">
            {/* Template Download */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Step 1: Download Template</h3>
              <Button
                variant="outline"
                onClick={downloadTemplate}
                className="flex items-center"
              >
                <Download className="h-4 w-4 mr-2" />
                Download CSV Template
              </Button>
              <p className="mt-2 text-xs text-gray-500">
                Use this template to format your prospect data correctly for import.
              </p>
            </div>

            {/* Upload Instructions */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Step 2: Prepare Your Data</h3>
              <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <FileText className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                    Use CSV format only
                  </li>
                  <li className="flex items-start">
                    <FileText className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                    Maximum file size: 50MB
                  </li>
                  <li className="flex items-start">
                    <FileText className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                    Required fields: First Name, Last Name, Email, Risk Score
                  </li>
                  <li className="flex items-start">
                    <FileText className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                    Numeric fields: Portfolio Value, Risk Score, Asset Allocation percentages
                  </li>
                </ul>
              </div>
            </div>

            {/* File Upload */}
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-2">Step 3: Upload File</h3>
              <div 
                className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
              >
                <div className="space-y-1 text-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex text-sm text-gray-600">
                    <label
                      htmlFor="file-upload"
                      className="relative cursor-pointer bg-white rounded-md font-medium text-primary hover:text-primary-dark focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary"
                    >
                      <span>Upload a file</span>
                      <input
                        id="file-upload"
                        name="file-upload"
                        type="file"
                        accept=".csv"
                        className="sr-only"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                      />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500">CSV up to 50MB</p>
                </div>
              </div>

              {error && (
                <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Error</h3>
                      <div className="mt-2 text-sm text-red-700">{error}</div>
                    </div>
                  </div>
                </div>
              )}

              {isValidating && (
                <div className="mt-4 text-center text-sm text-gray-600">
                  <div className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-solid border-primary border-r-transparent align-[-0.125em] mr-2"></div>
                  Validating file...
                </div>
              )}

              {isImporting && (
                <div className="mt-4">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium text-gray-700">Importing...</span>
                    <span className="text-sm text-gray-500">{importProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div 
                      className="bg-primary h-2.5 rounded-full" 
                      style={{ width: `${importProgress}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>

            {/* API Integration Option */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Alternative: API Integration</h3>
              <div className="bg-blue-50 rounded-lg p-4 text-sm">
                <div className="flex">
                  <Database className="h-5 w-5 text-blue-500 mt-0.5 mr-2" />
                  <div>
                    <p className="text-blue-700">
                      For automatic data import from whatismyrisknumber.com, set up the API integration in your settings.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2 text-xs py-1 h-auto"
                      onClick={() => {
                        onClose();
                        // Navigate to settings page
                        // In a real implementation, this would use a router to navigate
                        window.location.href = '/dashboard/settings';
                      }}
                    >
                      Configure API Integration
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportRiskProspectsModal;